import { v4 as uuidv4 } from 'uuid'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import { useFormik } from 'formik'
import FormikContext from '../../../context/formikContext'
import EmailFieldFormik from '../../formik/fields/emailFieldFormik'
import OutlinedFieldFormik from '../../formik/fields/outlinedFieldFormik'
import ContainedButton from '../../common/buttons/containedButton'
import SelectMultipleFormik from '../../formik/fields/selectMultipleFormik'
import TooltipRole from '../../../pages/entreprise/users/components/tooltipRole'
import { listRoles } from '../../../constants'
import { EnterprisesServerStateKeysEnum, useCreateUsers } from '../../../hooks/api/entreprises'
import { useContext, useState } from 'react'
import { ActiveEnterpriseContext } from '../../../context/activeEnterpriseContext'
import { UsersEnterpriseCreateType } from '../../../types/api/entreprise'
import { userEnterpriseSchema } from '../../../validations'
import { useQueryClient } from 'react-query'
import PlusIcon from '../../../icons/plusIcon'
import CloseIcon from '../../../icons/closeIcon'
import { useTheme, useMediaQuery } from '@mui/material'

const user = {
  email: '',
  first_name: '',
  last_name: '',
  roles: ['Utilisateur'],
}

const initialValues = {
  inviteList: [
    { id: uuidv4(), ...user },
  ],
}
interface InviteUsersFormProps {
  handleClose: () => void
  handleOpenSendInvitation: () => void
}

const InviteUsersForm = ({ handleClose, handleOpenSendInvitation }: InviteUsersFormProps) => {
  const [disabledSenButton, setDisabledSenButton] = useState(false)
  const { activeEnterprise } = useContext(ActiveEnterpriseContext)
  const query = useQueryClient()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down(800))
  const isSmallMobile = useMediaQuery(theme.breakpoints.down(480))

  const formik = useFormik({
    initialValues,
    validationSchema: userEnterpriseSchema,
    onSubmit: async (values) => {
      setDisabledSenButton(true)
      const { inviteList } = values
      const data = {
        crm_id: activeEnterprise.crm_id,
        users: inviteList,
      }
      await mutateUsers(data as UsersEnterpriseCreateType, {
        onSuccess: async () => {
          handleClose()
          handleOpenSendInvitation()
          await query.invalidateQueries(EnterprisesServerStateKeysEnum.Users)
        },
        onError: () => {
          handleClose()
        },
      })
    },
  })

  const { mutate: mutateUsers } = useCreateUsers(formik)

  const hasManyInvitations = formik.values.inviteList.length > 1
  const handleDeleteUser = (index: number) => {
    const updatedInviteList = [...formik.values.inviteList]
    updatedInviteList.splice(index, 1)
    formik.setValues({
      inviteList: updatedInviteList,
    })
  }

  const handleAddUser = () => {
    formik.setValues({
      inviteList: [...formik.values.inviteList, { id: uuidv4(), ...user }],
    })
  }

  return (
    <Stack spacing={isMobile ? '16px' : '24px'}>
      <Stack spacing={isMobile ? '24px' : '40px'}>
        {!isMobile && (
          <Stack direction='row' spacing='2%'>
            <Typography variant='smallText' width='26%' sx={{ textTransform: 'uppercase' }}>
              Courriel*
            </Typography>
            <Typography variant='smallText' width='23%' sx={{ textTransform: 'uppercase' }}>
              Prénom*
            </Typography>
            <Typography variant='smallText' width='23%' sx={{ textTransform: 'uppercase' }}>
              Nom*
            </Typography>
            <Box sx={{ position: 'relative' }}>
              <Typography variant='smallText' sx={{ textTransform: 'uppercase' }}>
                Rôles*
                <TooltipRole sx={{ position: 'absolute', top: '-13px', left: 'calc(100% - 7px)' }} />
              </Typography>
            </Box>
          </Stack>
        )}
        <FormikContext.Provider value={formik}>
          {formik.values.inviteList.map((user, index) => (
            <Box key={user.id}>
              {/* Mobile Layout */}
              {isMobile ? (
                <Stack spacing='16px' sx={{ p: '16px', border: '1px solid #E0E0E0', borderRadius: '8px', position: 'relative' }}>
                  {/* Delete button for mobile */}
                  {hasManyInvitations && (
                    <Box
                      component='button'
                      sx={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        border: 'none',
                        outline: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        p: '4px',
                        width: '24px',
                        height: '24px',
                        boxShadow: 'none',
                        borderRadius: '50%',
                        '&:hover': {
                          backgroundColor: '#f5f5f5',
                        },
                      }}
                      onClick={() => handleDeleteUser(index)}
                    >
                      <Box
                        sx={{
                          '& svg path': {
                            fill: '#F9A51A',
                          },
                        }}
                      >
                        <CloseIcon />
                      </Box>
                    </Box>
                  )}
                  
                  <Box>
                    <Typography variant='smallText' sx={{ textTransform: 'uppercase', mb: '8px', display: 'block' }}>
                      Courriel*
                    </Typography>
                    <EmailFieldFormik sx={{ width: '100%' }} name={`inviteList.${index}.email`} />
                  </Box>
                  
                  <Stack direction='row' spacing='12px'>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant='smallText' sx={{ textTransform: 'uppercase', mb: '8px', display: 'block' }}>
                        Prénom*
                      </Typography>
                      <OutlinedFieldFormik
                        sx={{ width: '100%' }}
                        name={`inviteList.${index}.first_name`}
                        placeholder={'Prénom'}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant='smallText' sx={{ textTransform: 'uppercase', mb: '8px', display: 'block' }}>
                        Nom*
                      </Typography>
                      <OutlinedFieldFormik
                        sx={{ width: '100%' }}
                        name={`inviteList.${index}.last_name`}
                        placeholder={'Nom de famille'}
                      />
                    </Box>
                  </Stack>
                  
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: '8px' }}>
                      <Typography variant='smallText' sx={{ textTransform: 'uppercase', mr: '8px' }}>
                        Rôles*
                      </Typography>
                      <TooltipRole />
                    </Box>
                    <SelectMultipleFormik
                      name={`inviteList.${index}.roles`}
                      disabledItems={['Utilisateur']}
                      listItems={listRoles}
                    />
                  </Box>
                </Stack>
              ) : (
                /* Desktop Layout */
                <Stack direction='row' spacing='2%' sx={{ alignItems: 'flex-start' }}>
                  <EmailFieldFormik sx={{ width: '26%' }} name={`inviteList.${index}.email`} />
                  <OutlinedFieldFormik
                    sx={{ width: '23%' }}
                    name={`inviteList.${index}.first_name`}
                    placeholder={'Votre prénom'}
                  />
                  <OutlinedFieldFormik
                    sx={{ width: '23%' }}
                    name={`inviteList.${index}.last_name`}
                    placeholder={'Votre nom de famille'}
                  />
                  <Box sx={{ width: '22%', display: 'flex', alignItems: 'center' }}>
                    <SelectMultipleFormik
                      name={`inviteList.${index}.roles`}
                      disabledItems={['Utilisateur']}
                      listItems={listRoles}
                    />

                    <Box
                      component='button'
                      disabled={!hasManyInvitations}
                      sx={{
                        border: 'none',
                        outline: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        ml: '19px',
                        mr: '10px',
                        p: '0',
                        width: '14px',
                        height: '14px',
                        boxShadow: 'none',
                      }}
                      onClick={() => handleDeleteUser(index)}
                    >
                      <Box
                        sx={{
                          '& svg path': {
                            fill: hasManyInvitations ? '#F9A51A' : '#b4b8b8',
                          },
                        }}
                      >
                        <CloseIcon />
                      </Box>
                    </Box>
                  </Box>
                </Stack>
              )}
            </Box>
          ))}
        </FormikContext.Provider>
      </Stack>
      <Button
        sx={{
          display: 'flex',
          width: 'auto',
          p: isMobile ? '12px' : '0',
          alignItems: 'center',
          '&:hover': {
            backgroundColor: isMobile ? '#f5f5f5' : 'white',
          },
          alignSelf: isMobile ? 'center' : 'start',
          textTransform: 'inherit',
          border: isMobile ? '1px dashed #ccc' : 'none',
          borderRadius: isMobile ? '8px' : '0',
          minHeight: isMobile ? '48px' : 'auto',
        }}
        onClick={handleAddUser}
      >
        <PlusIcon />
        <Typography
          variant={isMobile ? 'body1' : 'h5'}
          sx={{
            textDecoration: 'underline',
            fontWeight: '700',
            ml: '15px',
            fontSize: isMobile ? '14px' : undefined,
          }}
        >
          Ajouter un autre utilisateur
        </Typography>
      </Button>
      <Box
        sx={{
          bottom: 0,
          zIndex: 10,
          bgcolor: 'white',
          borderTop: '1px solid #e0e0e0',
          marginTop: '32px',
          paddingTop: '24px',
          borderBottomLeftRadius: {
            xs: '12px',
            md: '8px',
          },
          borderBottomRightRadius: {
            xs: '12px',
            md: '8px',
          },
        }}
      >
        <ContainedButton
          sx={{ 
            width: isMobile ? '100%' : '256px', 
            px: '8px',
            py: isMobile ? '10px' : '12px', 
            alignSelf: isMobile ? 'stretch' : 'end',
            minHeight: isMobile ? '48px' : 'auto',
            mb: isSmallMobile ? '16px' : '24px',
            ml: isMobile ? 'auto' : '0',
            mr: isMobile ? 'auto' : '0',
          }}
          title='envoyer les invitations'
          disabled={disabledSenButton || !formik.isValid || formik.isSubmitting}
          handleClick={formik.handleSubmit}
        />
      </Box>
    </Stack>
  )
}

export default InviteUsersForm
